import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, policiesAPI } from '../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import UserModal from '../components/UserModal';
import UserProfileModal from '../components/UserProfileModal';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import { getDepartmentFilterConfig } from './userManagement/getDepartmentFilterConfig';

function UserManagement() {
  const { isSuperAdmin, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // For dropdown
  const [departments, setDepartments] = useState([]);
  const [workingPolicies, setWorkingPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  // Stats modal state
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [statsUser, setStatsUser] = useState(null);
  
  // Confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    status: '',
    page: 1,
    limit: 20
  });

  const departmentFilterConfig = getDepartmentFilterConfig({
    user,
    isSuperAdmin: isSuperAdmin(),
    departments
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getUsers(filters);
      setUsers(response.data.users || []);
      setError(null);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalCount: response.data.totalCount
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [departmentsResponse, policiesResponse, allUsersResponse] = await Promise.all([
        usersAPI.getDepartmentsWithCount(),
        policiesAPI.getWorkingHoursPolicies(),
        usersAPI.getUsers({ limit: 1000 }) // Get all users for dropdown
      ]);
      
      // Extract department names from the response
      const departmentNames = departmentsResponse.data?.departments?.map(dept => dept.name) || [];
      console.log('Loaded departments:', departmentNames);
      setDepartments(departmentNames);
      setWorkingPolicies(policiesResponse.data || []);
      setAllUsers(allUsersResponse.data.users || []);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!departmentFilterConfig.selectedDepartment) {
      return;
    }

    setFilters((current) => {
      if (current.department === departmentFilterConfig.selectedDepartment) {
        return current;
      }

      return {
        ...current,
        department: departmentFilterConfig.selectedDepartment,
        page: 1
      };
    });
  }, [departmentFilterConfig.selectedDepartment]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      department: departmentFilterConfig.selectedDepartment,
      status: '',
      page: 1,
      limit: 20
    });
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleViewStats = (user) => {
    setStatsUser(user);
    setIsStatsOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : 'this user';
    
    setConfirmConfig({
      title: 'Delete User',
      message: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await usersAPI.deleteUser(userId);
          loadUsers(); // Refresh the list
          toast.success('User deleted successfully');
        } catch (err) {
          toast.error('Failed to delete user: ' + err.message);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleModalSave = async (userData) => {
    try {
      const isCreateMode = modalMode === 'create';

      if (modalMode === 'create') {
        await usersAPI.createUser(userData);
      } else {
        await usersAPI.updateUser(selectedUser.id, userData);
      }
      
      setIsModalOpen(false);
      setSelectedUser(null);

      const shouldResetToFirstPage = isCreateMode &&
        filters.page !== 1 &&
        !filters.search &&
        !filters.department &&
        !filters.status;

      if (shouldResetToFirstPage) {
        setFilters(prev => ({
          ...prev,
          page: 1
        }));
      } else {
        await loadUsers();
      }

      await loadInitialData();
      toast.success(isCreateMode ? 'User created successfully' : 'User updated successfully');
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to save user';
      const details = err.response?.data?.details || null;
      const fields = err.response?.data?.fields || null;
      const code = err.response?.data?.code || null;
      const subordinates = err.response?.data?.subordinates || null;
      const saveError = new Error(message);
      saveError.details = details;
      saveError.fields = fields;
      saveError.code = code;
      saveError.subordinates = subordinates;
      throw saveError;
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Active': 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md',
      'Not Active': 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-md',
      'Restricted': 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow-md'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1.5 text-xs font-bold rounded-lg ${
        statusStyles[status] || 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-md'
      }`}>
        {status === 'Active' ? '✓ Active' : status === 'Not Active' ? '✗ Inactive' : status}
      </span>
    );
  };

  const isAdminUser = (user) => {
    return user.officialEmail === 'admin@mastersunion.org';
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header - Modern Gradient Design */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 rounded-2xl shadow-2xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <div className="h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-2xl ring-4 ring-white/10 mr-5">
              <UserCircleIcon className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold drop-shadow-lg">User Management</h1>
              <p className="text-white/90 mt-1 text-lg font-medium">
                {pagination.totalCount} employees • Manage accounts and profiles
              </p>
            </div>
          </div>
          <button
            onClick={handleCreateUser}
            className="w-full sm:w-auto group inline-flex items-center justify-center px-6 py-3 border-2 border-white/30 text-base font-bold rounded-xl text-purple-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-white/50 transition-all duration-200 shadow-2xl sm:hover:scale-105"
          >
            <PlusIcon className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Add User
          </button>
        </div>
      </div>

      {/* Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 mr-3" />
              <h3 className="text-lg font-bold text-gray-900">Search & Filters</h3>
            </div>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-200 border border-purple-200"
            >
              Clear all
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
                <input
                type="text"
                placeholder="Search by full name, email, UID, employee ID..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Department */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              disabled={departmentFilterConfig.disableSelection}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              {departmentFilterConfig.showAllDepartmentsOption && <option value="">All Departments</option>}
              {departmentFilterConfig.options.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Not Active">Not Active</option>
              <option value="Restricted">Restricted</option>
            </select>
          </div>
        </div>
        </div>
      </div>

      {/* Users List - Modern Design */}
      <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-md mr-3">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Employee Directory
                </h3>
                <p className="text-xs text-gray-500">{pagination.totalCount} total users</p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">Error: {error}</div>
              <button
                onClick={loadUsers}
                className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new user.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleCreateUser}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add User
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="space-y-4 md:hidden">
                {users.map((user) => (
                  <div key={user.id} className="bg-white rounded-xl border-2 border-gray-200 shadow-sm p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center shadow ring-2 ring-white overflow-hidden">
                          {user.profilePhotoUrl ? (
                            <img
                              src={user.profilePhotoUrl}
                              alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-bold text-white">
                              {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                            </span>
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="flex items-center gap-2">
                            <div className="text-base font-semibold text-gray-900">
                              {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}
                            </div>
                            {isAdminUser(user) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                🔒 Admin
                              </span>
                            )}
                            {user.excludeFromAttendanceStats && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
                                Stats excluded
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.officialEmail}</div>
                        </div>
                      </div>
                      <div className="ml-3">{getStatusBadge(user.userStatus)}</div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-500">Department</div>
                      <div className="text-gray-900 text-right">{user.Department || 'N/A'}</div>
                      <div className="text-gray-500">Employment</div>
                      <div className="text-gray-900 text-right">{user.employementType || '—'}</div>
                      <div className="text-gray-500">UID</div>
                      <div className="text-gray-900 text-right">{user.uidNumber || 'N/A'}</div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      {!isAdminUser(user) && (
                        <button
                          onClick={() => handleEditUser(user)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" /> Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleViewStats(user)}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100"
                      >
                        <UserCircleIcon className="h-4 w-4 mr-1" /> Stats
                      </button>
                      {!isAdminUser(user) && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="inline-flex items-center justify-center px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto rounded-xl border-2 border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-purple-50 to-fuchsia-50 sticky top-0 z-10 shadow-md">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        Employee
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        Department
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        Employment Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        UID Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-purple-900 uppercase tracking-wider border-b-2 border-purple-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-fuchsia-50/50 transition-all duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center shadow-md ring-2 ring-white overflow-hidden">
                                {user.profilePhotoUrl ? (
                                  <img
                                    src={user.profilePhotoUrl}
                                    alt={`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-bold text-white">
                                    {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900">
                                  {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}
                                </div>
                                {isAdminUser(user) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                    🔒 Admin
                                  </span>
                                )}
                                {user.excludeFromAttendanceStats && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900 border border-amber-300">
                                    Stats excluded
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.officialEmail}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.Department || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(user.userStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md border ${
                            user.employementType === 'Full-Time' 
                              ? 'bg-blue-50 text-blue-700 border-blue-200' 
                              : 'bg-purple-50 text-purple-700 border-purple-200'
                          }`}>
                            {user.employementType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.uidNumber || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {!isAdminUser(user) && (
                              <button
                                onClick={() => handleEditUser(user)}
                                className="inline-flex items-center p-2 text-blue-600 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 rounded-lg transition-all duration-200 hover:shadow-md"
                                title="Edit User"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleViewStats(user)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-all duration-200 hover:shadow-md"
                              title="View Stats"
                            >
                              <UserCircleIcon className="h-4 w-4 mr-1" />
                              Stats
                            </button>
                            {!isAdminUser(user) && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="inline-flex items-center p-2 text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 rounded-lg transition-all duration-200 hover:shadow-md"
                                title="Delete User"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(pagination.currentPage - 1) * filters.limit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.currentPage * filters.limit, pagination.totalCount)}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">{pagination.totalCount}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                        <button
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                          if (pageNum > pagination.totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                pageNum === pagination.currentPage
                                  ? 'z-10 bg-primary-600 text-white'
                                  : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        user={selectedUser}
        mode={modalMode}
        departments={departments}
        workingPolicies={workingPolicies}
        users={allUsers} // For reporting manager dropdown - all users
        canEditAttendanceExclusion={isSuperAdmin()}
      />

      {/* User Stats Modal */}
      <UserProfileModal
        isOpen={isStatsOpen}
        onClose={() => { setIsStatsOpen(false); setStatsUser(null); }}
        user={statsUser}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText || 'Confirm'}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
      />
    </div>
  );
}

export default UserManagement;
